import { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faXmark,
  faCheck,
  faSpinner,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Title from "../shared/components/Title";
import apiClient from "../utils/apiClient";

const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "mapped", label: "Mapped" },
  { key: "unmapped", label: "Unmapped" },
];

export default function CarrierServiceMap() {
  const [services, setServices] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [savingNk, setSavingNk] = useState(null);
  const [confirmDeleteNk, setConfirmDeleteNk] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setIsLoading(true);
    try {
      const [typesRes, servicesRes] = await Promise.all([
        apiClient.get("/api/v1/carrierServiceMap/types"),
        apiClient.get("/api/v1/carrierServiceMap/services"),
      ]);
      const [typesData, servicesData] = await Promise.all([
        typesRes.json(),
        servicesRes.json(),
      ]);
      setServiceTypes(Array.isArray(typesData) ? typesData : []);
      setServices(Array.isArray(servicesData) ? servicesData : []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }

  const filtered = useMemo(() => {
    let result = services;

    if (activeFilter === "mapped") result = result.filter((s) => s.isMapped);
    else if (activeFilter === "unmapped") result = result.filter((s) => !s.isMapped);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.carrierServiceNk?.toLowerCase().includes(q) ||
          s.carrierServiceName?.toLowerCase().includes(q) ||
          s.carrierName?.toLowerCase().includes(q) ||
          s.serviceTypeName?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [services, activeFilter, searchQuery]);

  async function handleServiceTypeChange(nk, serviceTypeName) {
    if (!serviceTypeName) return;
    setSavingNk(nk);
    try {
      const res = await apiClient.put("/api/v1/carrierServiceMap/", {
        carrier_service_nk: nk,
        service_type_name: serviceTypeName,
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
        return;
      }
      setServices((prev) =>
        prev.map((s) =>
          s.carrierServiceNk === nk
            ? { ...s, serviceTypeName, isMapped: true }
            : s
        )
      );
      toast.success("Saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingNk(null);
    }
  }

  async function handleConfirmDelete() {
    if (!confirmDeleteNk) return;
    setIsDeleting(true);
    try {
      const res = await apiClient.delete(
        `/api/v1/carrierServiceMap/${encodeURIComponent(confirmDeleteNk)}`
      );
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete");
        return;
      }
      toast.success("Mapping removed");
      setConfirmDeleteNk(null);
      setServices((prev) =>
        prev.map((s) =>
          s.carrierServiceNk === confirmDeleteNk
            ? { ...s, serviceTypeName: null, isMapped: false, updatedAt: null, updatedBy: null }
            : s
        )
      );
    } catch {
      toast.error("Failed to delete");
    } finally {
      setIsDeleting(false);
    }
  }

  const mappedCount = services.filter((s) => s.isMapped).length;
  const unmappedCount = services.filter((s) => !s.isMapped).length;

  return (
    <div className="p-6">
      <Title title="Carrier Service Map" />

      {/* Stats */}
      {!isLoading && (
        <div className="mt-4 flex gap-3">
          <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-sm">
            <span className="font-semibold text-green-700">{mappedCount}</span>
            <span className="text-green-600 ml-1">mapped</span>
          </div>
          <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <span className="font-semibold text-amber-700">{unmappedCount}</span>
            <span className="text-amber-600 ml-1">unmapped</span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="mt-4 mb-4 flex items-center gap-3 flex-wrap">
        {/* Filter tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeFilter === tab.key
                  ? "bg-white text-[#144677] shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4"
          />
          <input
            type="text"
            placeholder="Search carrier or service..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <FontAwesomeIcon icon={faSpinner} spin className="w-8 h-8 text-[#144677]" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
              <thead className="bg-gray-50">
                <tr>
                  {["Carrier Service", "Carrier", "Service Type", "Updated At", "Updated By", ""].map(
                    (h, i) => (
                      <th
                        key={i}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                      No carrier services found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => (
                    <tr key={s.carrierServiceNk} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-800">{s.carrierServiceName}</p>
                        <p className="text-xs text-gray-400 font-mono mt-0.5">{s.carrierServiceNk}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {s.carrierName || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={s.serviceTypeName || ""}
                            disabled={savingNk === s.carrierServiceNk}
                            onChange={(e) =>
                              handleServiceTypeChange(s.carrierServiceNk, e.target.value)
                            }
                            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677] disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                          >
                            <option value="">— unassigned —</option>
                            {serviceTypes.map((t) => (
                              <option key={t.serviceTypeName} value={t.serviceTypeName}>
                                {t.serviceTypeName}
                              </option>
                            ))}
                          </select>
                          {savingNk === s.carrierServiceNk && (
                            <FontAwesomeIcon
                              icon={faSpinner}
                              spin
                              className="w-3.5 h-3.5 text-[#144677]"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {s.updatedAt
                          ? new Date(s.updatedAt).toLocaleDateString("en-AU")
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {s.updatedBy || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {s.isMapped && (
                          <button
                            onClick={() => setConfirmDeleteNk(s.carrierServiceNk)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Remove mapping"
                          >
                            <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            {filtered.length} of {services.length} services
          </p>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteNk && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Remove Mapping</h2>
            <p className="text-sm text-gray-600 mb-6">
              Remove the service type mapping for{" "}
              <span className="font-mono font-medium text-gray-900">{confirmDeleteNk}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteNk(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {isDeleting && (
                  <FontAwesomeIcon icon={faSpinner} spin className="w-3.5 h-3.5" />
                )}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
